import React, { useRef, useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { X, Minus, Maximize2 } from "lucide-react";
import { useLanguage } from "@/i18n";

/**
 * Reusable draggable paper window.
 * Props:
 *  id, title, subtitle, initial {x,y,rotate,w}, z, onFocus, onClose, onMinimize,
 *  decoration: 'tape' | 'tape-double' | 'clip' | 'pin' | 'none'
 *  variant: 'paper' | 'tracing'
 *  isMobile: bool
 */
export const PaperWindow = ({
  id,
  title,
  subtitle,
  children,
  initial = { x: 0, y: 0, rotate: 0, w: 420 },
  z = 1,
  onFocus,
  onClose,
  onMinimize,
  decoration = "tape",
  variant = "paper",
  isMobile = false,
  testId,
  className = "",
}) => {
  const { t } = useLanguage();
  const reduce = useReducedMotion();
  const constraintsRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  const baseRotate = initial.rotate || 0;

  // Mobile: render as static stacked card
  if (isMobile) {
    return (
      <section
        data-testid={testId || `window-${id}`}
        className={`paper paper-corner-fold relative w-full mb-8 ${className}`}
        style={{ padding: 0 }}
      >
        {decoration !== "none" && <Decoration type={decoration} />}
        <Header title={title} subtitle={subtitle} mobile />
        <div className="px-5 pb-6 pt-2">{children}</div>
      </section>
    );
  }

  return (
    <motion.div
      data-testid={testId || `window-${id}`}
      drag
      dragMomentum={false}
      dragElastic={0.08}
      onMouseDown={() => onFocus && onFocus(id)}
      onTouchStart={() => onFocus && onFocus(id)}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 30, rotate: baseRotate }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, rotate: baseRotate }}
      transition={{ type: "spring", stiffness: 110, damping: 18, delay: (initial.delay || 0) }}
      whileDrag={reduce ? {} : { scale: 1.01, rotate: baseRotate + 1.2, zIndex: 999, cursor: "grabbing" }}
      whileHover={reduce ? {} : { y: -3, transition: { duration: 0.2 } }}
      style={{
        position: "absolute",
        left: initial.x,
        top: initial.y,
        width: initial.w,
        zIndex: z,
        rotate: baseRotate,
      }}
      className={`draggable-window ${variant === "tracing" ? "paper-tracing" : "paper paper-corner-fold"} group ${className}`}
    >
      {decoration !== "none" && <Decoration type={decoration} />}

      <Header
        title={title}
        subtitle={subtitle}
        onClose={onClose ? () => onClose(id) : undefined}
        onMinimize={onMinimize ? () => onMinimize(id) : undefined}
        closeLabel={t("close")}
        minimizeLabel={t("minimize")}
      />

      <div className="px-6 pb-6 pt-2">{children}</div>

      {/* Subtle stain (decorative) */}
      {hovered && (
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-1 right-8 h-2 w-16 rounded-full"
          style={{ background: "radial-gradient(ellipse at center, rgba(180,138,59,0.18), transparent 70%)" }}
        />
      )}
    </motion.div>
  );
};

const Header = ({ title, subtitle, onClose, onMinimize, mobile, closeLabel = "Close", minimizeLabel = "Minimize" }) => (
  <div className={`flex items-start justify-between gap-3 border-b border-[var(--paper-edge)] ${mobile ? "px-5 pt-5 pb-3" : "px-6 pt-6 pb-3"}`}>
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint">
        {subtitle || "sheet"}
      </p>
      <h2 className="font-display text-xl font-medium tracking-tight text-ink mt-1">
        {title}
      </h2>
    </div>
    {(onClose || onMinimize) && !mobile && (
      <div className="flex items-center gap-1.5 mt-1">
        {onMinimize && (
          <button
            data-testid={`btn-minimize-${title}`}
            onClick={onMinimize}
            aria-label={minimizeLabel}
            className="h-6 w-6 grid place-items-center rounded-full text-ink-faint hover:text-ink hover:bg-[var(--bg-board-2)] transition"
          >
            <Minus size={13} />
          </button>
        )}
        {onClose && (
          <button
            data-testid={`btn-close-${title}`}
            onClick={onClose}
            aria-label={closeLabel}
            className="h-6 w-6 grid place-items-center rounded-full text-ink-faint hover:text-accentRed hover:bg-[var(--bg-board-2)] transition"
          >
            <X size={13} />
          </button>
        )}
      </div>
    )}
  </div>
);

const Decoration = ({ type }) => {
  if (type === "tape") {
    return (
      <span
        className="tape"
        style={{ top: -10, left: "50%", transform: "translateX(-50%) rotate(-2deg)" }}
        aria-hidden
      />
    );
  }
  if (type === "tape-double") {
    return (
      <>
        <span className="tape" style={{ top: -10, left: 28, transform: "rotate(-6deg)" }} aria-hidden />
        <span className="tape" style={{ top: -10, right: 28, transform: "rotate(4deg)" }} aria-hidden />
      </>
    );
  }
  if (type === "clip") {
    return <span className="paperclip" style={{ top: -22, left: 30 }} aria-hidden />;
  }
  if (type === "pin") {
    return <span className="pushpin" style={{ top: 8, left: "50%", transform: "translateX(-50%)" }} aria-hidden />;
  }
  return null;
};

export default PaperWindow;
